import React, { useState, useRef, useEffect } from 'react';
import { chatService, streamMessage } from '../../../services/api';
import { format } from 'date-fns';
import styles from './FloatingChat.module.css';
import VoiceChat from '../VoiceChat/VoiceChat';

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const modalRef = useRef(null);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const voiceChatRef = useRef(null);
  const cancelStreamRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      
      // Cancel any ongoing stream when component unmounts
      if (cancelStreamRef.current) {
        cancelStreamRef.current();
        cancelStreamRef.current = null;
      }
      
      // Stop any ongoing speech
      if (voiceChatRef.current) {
        voiceChatRef.current.stopSpeaking();
      }
      window.speechSynthesis.cancel();
    };
  }, []);

  // Handle chat open/close
  useEffect(() => {
    if (!isOpen) {
      // When chat is closed, stop any ongoing streams and speech
      if (cancelStreamRef.current) {
        cancelStreamRef.current();
        cancelStreamRef.current = null;
      }
      
      if (voiceChatRef.current) {
        voiceChatRef.current.stopSpeaking();
      }
      window.speechSynthesis.cancel();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);

    const newMessage = {
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);

    // Add an empty AI message that will be updated as tokens arrive
    const aiMessageId = Date.now().toString();
    setMessages(prev => [...prev, {
      id: aiMessageId,
      type: 'ai',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      isGenerating: true
    }]);

    // Cancel any existing stream
    if (cancelStreamRef.current) {
      cancelStreamRef.current();
      cancelStreamRef.current = null;
    }

    try {
      // Use streaming API with better error handling
      cancelStreamRef.current = await streamMessage(
        userMessage,
        // onToken callback - update the AI message with each new token
        (token) => {
          setMessages(prev => {
            const updatedMessages = [...prev];
            const aiMessageIndex = updatedMessages.findIndex(msg => msg.id === aiMessageId);
            
            if (aiMessageIndex !== -1) {
              updatedMessages[aiMessageIndex] = {
                ...updatedMessages[aiMessageIndex],
                content: updatedMessages[aiMessageIndex].content + token,
                isGenerating: false
              };
            }
            
            return updatedMessages;
          });
        },
        // onComplete callback
        () => {
          setIsLoading(false);
          // Mark the message as no longer streaming
          setMessages(prev => {
            const updatedMessages = [...prev];
            const aiMessageIndex = updatedMessages.findIndex(msg => msg.id === aiMessageId);
            
            if (aiMessageIndex !== -1) {
              const completedMessage = {
                ...updatedMessages[aiMessageIndex],
                isStreaming: false,
                isGenerating: false
              };
              updatedMessages[aiMessageIndex] = completedMessage;
              
              // If in voice mode, speak the completed message
              if (isVoiceMode && voiceChatRef.current) {
                setTimeout(() => {
                  handleVoiceSpeaking(completedMessage.content);
                }, 500); // Small delay to ensure UI is updated first
              }
            }
            
            return updatedMessages;
          });
          
          // Clear the cancel reference
          cancelStreamRef.current = null;
        },
        // onError callback
        (error) => {
          console.error('Chat streaming error:', error);
          setIsLoading(false);
          
          // Update the AI message to show the error
          setMessages(prev => {
            const updatedMessages = [...prev];
            const aiMessageIndex = updatedMessages.findIndex(msg => msg.id === aiMessageId);
            
            if (aiMessageIndex !== -1) {
              updatedMessages[aiMessageIndex] = {
                type: 'error',
                content: 'Failed to get response. Please try again.',
                timestamp: new Date(),
                isStreaming: false,
                isGenerating: false
              };
            }
            
            return updatedMessages;
          });
          
          // Clear the cancel reference
          cancelStreamRef.current = null;
        }
      );
    } catch (error) {
      console.error('Chat setup error:', error);
      setIsLoading(false);
      
      // Update the AI message to show the error
      setMessages(prev => {
        const updatedMessages = [...prev];
        const aiMessageIndex = updatedMessages.findIndex(msg => msg.id === aiMessageId);
        
        if (aiMessageIndex !== -1) {
          updatedMessages[aiMessageIndex] = {
            type: 'error',
            content: 'Failed to get response. Please try again.',
            timestamp: new Date()
          };
        }
        
        return updatedMessages;
      });
    }
  };

  const handleVoiceMessage = async (transcript) => {
    if (!transcript.trim()) return;

    // Stop any ongoing speech when user starts talking
    if (voiceChatRef.current) {
      voiceChatRef.current.stopSpeaking();
    }

    // Cancel any ongoing stream
    if (cancelStreamRef.current) {
      cancelStreamRef.current();
      cancelStreamRef.current = null;
    }

    const userMessage = {
      type: 'user',
      content: transcript,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    // Add an empty AI message that will be updated as tokens arrive
    const aiMessageId = Date.now().toString();
    setMessages(prev => [...prev, {
      id: aiMessageId,
      type: 'ai',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      isGenerating: true
    }]);

    try {
      // Use streaming API with better error handling
      cancelStreamRef.current = await streamMessage(
        transcript,
        // onToken callback - update the AI message with each new token
        (token) => {
          setMessages(prev => {
            const updatedMessages = [...prev];
            const aiMessageIndex = updatedMessages.findIndex(msg => msg.id === aiMessageId);
            
            if (aiMessageIndex !== -1) {
              updatedMessages[aiMessageIndex] = {
                ...updatedMessages[aiMessageIndex],
                content: updatedMessages[aiMessageIndex].content + token,
                isGenerating: false
              };
            }
            
            return updatedMessages;
          });
        },
        // onComplete callback
        () => {
          // Mark the message as no longer streaming
          setMessages(prev => {
            const updatedMessages = [...prev];
            const aiMessageIndex = updatedMessages.findIndex(msg => msg.id === aiMessageId);
            
            if (aiMessageIndex !== -1) {
              const completedMessage = {
                ...updatedMessages[aiMessageIndex],
                isStreaming: false,
                isGenerating: false
              };
              updatedMessages[aiMessageIndex] = completedMessage;
              
              // If in voice mode, speak the completed message
              if (isVoiceMode && voiceChatRef.current) {
                setTimeout(() => {
                  handleVoiceSpeaking(completedMessage.content);
                }, 500); // Small delay to ensure UI is updated first
              }
            }
            
            return updatedMessages;
          });
          
          // Clear the cancel reference
          cancelStreamRef.current = null;
        },
        // onError callback
        (error) => {
          console.error('Voice chat streaming error:', error);
          
          // Update the AI message to show the error
          setMessages(prev => {
            const updatedMessages = [...prev];
            const aiMessageIndex = updatedMessages.findIndex(msg => msg.id === aiMessageId);
            
            if (aiMessageIndex !== -1) {
              updatedMessages[aiMessageIndex] = {
                type: 'error',
                content: 'Failed to get response. Please try again.',
                timestamp: new Date(),
                isStreaming: false,
                isGenerating: false
              };
            }
            
            return updatedMessages;
          });
          
          // Clear the cancel reference
          cancelStreamRef.current = null;
        }
      );
    } catch (error) {
      console.error('Voice chat setup error:', error);
      
      // Update the AI message to show the error
      setMessages(prev => {
        const updatedMessages = [...prev];
        const aiMessageIndex = updatedMessages.findIndex(msg => msg.id === aiMessageId);
        
        if (aiMessageIndex !== -1) {
          updatedMessages[aiMessageIndex] = {
            type: 'error',
            content: 'Failed to get response. Please try again.',
            timestamp: new Date()
          };
        }
        
        return updatedMessages;
      });
    }
  };

  // Helper function to handle voice speaking
  const handleVoiceSpeaking = async (text) => {
    if (!text || !isVoiceMode || !voiceChatRef.current) return;
    
    try {
      // Stop any ongoing speech first
      voiceChatRef.current.stopSpeaking();
      
      const maxChunkLength = 150; // Reduced chunk size
      
      if (text.length > maxChunkLength) {
        // Split by sentences more carefully
        const sentences = text.split(/(?<=[.!?])\s+/)
          .filter(sentence => sentence.trim().length > 0);
        
        for (const sentence of sentences) {
          if (!isVoiceMode) break; // Stop if voice mode was turned off
          await voiceChatRef.current.speak(sentence.trim());
        }
      } else {
        await voiceChatRef.current.speak(text);
      }
    } catch (error) {
      console.error('Error in voice response:', error);
      // Don't show an error to the user, just log it
    }
  };

  const formatTimestamp = (timestamp) => {
    return format(new Date(timestamp), 'h:mm a');
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Add a toggle for voice mode
  const toggleVoiceMode = () => {
    // If turning off voice mode, stop any ongoing speech
    if (isVoiceMode) {
      if (voiceChatRef.current) {
        voiceChatRef.current.stopSpeaking();
      }
      window.speechSynthesis.cancel();
    }
    
    setIsVoiceMode(!isVoiceMode);
  };

  // Add a function to stop the current stream
  const handleStopGeneration = () => {
    if (cancelStreamRef.current) {
      // Call the cancel function to abort the stream
      cancelStreamRef.current();
      cancelStreamRef.current = null;
      
      // Update the loading state
      setIsLoading(false);
      
      // Update the last AI message to mark it as no longer streaming
      setMessages(prev => {
        const updatedMessages = [...prev];
        // Find the last streaming AI message
        let lastAiMessageIndex = -1;
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].type === 'ai' && updatedMessages[i].isStreaming) {
            lastAiMessageIndex = i;
            break;
          }
        }
        
        if (lastAiMessageIndex !== -1) {
          const content = updatedMessages[lastAiMessageIndex].content;
          updatedMessages[lastAiMessageIndex] = {
            ...updatedMessages[lastAiMessageIndex],
            isStreaming: false,
            isGenerating: false,
            content: content && content.trim() ? content : "Generation stopped by user."
          };
        }
        
        return updatedMessages;
      });
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button 
        className={styles.floatingButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Chat with AI Assistant"
      >
        <div className={styles.chatIconWrapper}>
          <i className="fas fa-comments"></i>
          {!isOpen && <span className={styles.pulseRing}></span>}
        </div>
      </button>

      {/* Chat Modal */}
      {isOpen && (
        <div className={`${styles.modalWrapper} ${isMaximized ? styles.maximized : ''}`}>
          <div className={`${styles.modal} ${isMaximized ? styles.maximized : ''}`} ref={modalRef}>
            {/* Modal Header */}
            <div className={styles.modalHeader}>
              <div className={styles.headerContent}>
                <div className={styles.headerLeft}>
                  <i className="fas fa-comments"></i>
                  <div>
                    <h3>AI Assistant</h3>
                    <p>Online</p>
                  </div>
                </div>
                <div className={styles.headerControls}>
                  <button 
                    className={styles.maximizeButton}
                    onClick={toggleMaximize}
                    aria-label={isMaximized ? "Minimize chat" : "Maximize chat"}
                  >
                    <i className={`fas ${isMaximized ? 'fa-compress-alt' : 'fa-expand-alt'}`}></i>
                  </button>
                  <button 
                    className={styles.closeButton}
                    onClick={() => setIsOpen(false)}
                    aria-label="Close chat"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className={styles.messagesContainer}>
              {isVoiceMode ? (
                <VoiceChat 
                  ref={voiceChatRef}
                  onSendMessage={handleVoiceMessage}
                  isOpen={isOpen}
                />
              ) : (
                messages.length === 0 ? (
                  <div className={styles.welcomeMessage}>
                    <div className={styles.welcomeIcon}>
                      <i className="fas fa-robot"></i>
                    </div>
                    <h4>Hello! 👋</h4>
                    <p>Ask me anything about our services or how I can assist you.</p>
                  </div>
                ) : (
                  <div className={styles.messages}>
                    {messages.map((msg, index) => (
                      <div key={index} className={`${styles.message} ${styles[msg.type]}`}>
                        <div className={styles.messageContent}>
                          <div className={styles.messageAvatar}>
                            {msg.type === 'user' ? (
                              <i className="fas fa-user"></i>
                            ) : (
                              <i className="fas fa-robot"></i>
                            )}
                          </div>
                          <div className={styles.messageBody}>
                            <div className={styles.messageText}>
                              {msg.isGenerating ? 'Generating...' : msg.content}
                              {msg.isStreaming && !msg.isGenerating && (
                                <span className={styles.typingIndicator}>
                                  <span className={styles.dot}></span>
                                  <span className={styles.dot}></span>
                                  <span className={styles.dot}></span>
                                </span>
                              )}
                            </div>
                            <div className={styles.timestamp}>
                              {formatTimestamp(msg.timestamp)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )
              )}
            </div>

            {/* Modified Input Area */}
            <div className={styles.inputArea}>
              <form onSubmit={handleSubmit}>
                {!isVoiceMode && (
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your message..."
                    disabled={isLoading}
                  />
                )}
                <div className={styles.buttonGroup}>
                  {/* Show Stop Generation button during streaming */}
                  {cancelStreamRef.current && (
                    <button
                      type="button"
                      className={styles.stopGenerationButton}
                      onClick={handleStopGeneration}
                      title="Stop generation"
                    >
                      <i className="fas fa-stop"></i>
                    </button>
                  )}
                  
                  {/* Voice mode toggle button - always visible */}
                  <button
                    type="button"
                    className={styles.voiceModeToggle}
                    onClick={toggleVoiceMode}
                    title={isVoiceMode ? "Switch to text" : "Switch to voice"}
                  >
                    <i className={`fas ${isVoiceMode ? 'fa-keyboard' : 'fa-microphone'}`}></i>
                  </button>
                  
                  {/* Stop speaking button - only in voice mode */}
                  {isVoiceMode && (
                    <button
                      type="button"
                      className={styles.stopButton}
                      onClick={() => voiceChatRef.current?.stopSpeaking()}
                      title="Stop speaking"
                    >
                      <i className="fas fa-stop"></i>
                    </button>
                  )}
                  
                  {/* Send button - only show when not generating a response */}
                  {!isVoiceMode && !cancelStreamRef.current && (
                    <button 
                      type="submit" 
                      disabled={isLoading || !input.trim()}
                      title={isLoading ? 'Sending...' : 'Send message'}
                    >
                      {isLoading ? (
                        <div className={styles.sendingIndicator}>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      ) : (
                        <i className="fas fa-paper-plane"></i>
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingChat; 